import { getToast } from "../../../common/utils";
import {
  listContractReviewClauseManagement,
  listTalkToContractClauseManagement,
  saveContractReviewClauseManagement,
  saveTalkToContractClauseManagement,
} from "../admin.service";
import { defaultClauseStatus, PromptsForFeature } from "../constants";
import { PromptModel, PromptModelForContractReview } from "../model";
import {
  getClauseItemsForContractReview,
  getClauseItemsForTTC,
  getRuleForSavingForContractReview,
  getRuleForSavingForTTC,
} from "../util";

export const ADMIN_ACTIONS = {
  GET_PROMPTS: "GET_PROMPTS",
  UPDATE_CLAUSE: "UPDATE_CLAUSE",
  ADD_NEW_KEYWORD_SECTION_FOR_TTC: "ADD_NEW_KEYWORD_SECTION_FOR_TTC",
  SAVE_NEW_SYNONYM_FOR_TTC: "SAVE_NEW_SYNONYM_FOR_TTC",
  DELETE_KEYWORD_SECTION_FOR_TTC: "DELETE_KEYWORD_SECTION_FOR_TTC",
  REMOVE_KEYWORDS_WITH_NEW_KEYWORD_TYPE:
    "REMOVE_KEYWORDS_WITH_NEW_KEYWORD_TYPE",
  SET_ROLE_MANAGE_DATA: "SET_ROLE_MANAGE_DATA",
};

const promptManagement = {
  [PromptsForFeature.CLAUSE_CHECK]: {},
  [PromptsForFeature.CONTRACT_REVIEW]: {
    api: listContractReviewClauseManagement,
    model: PromptModelForContractReview,
    sanitizer: getClauseItemsForContractReview,
  },
  [PromptsForFeature.TALK_TO_CONTRACT]: {
    api: listTalkToContractClauseManagement,
    model: PromptModel,
    sanitizer: getClauseItemsForTTC,
  },
};

export const getPrompts = ({ feature }: { feature: string }) => {
  return async (dispatch: any) => {
    try {
      dispatch({
        type: ADMIN_ACTIONS.GET_PROMPTS,
        payload: {
          feature: feature,
          data: {
            loading: true,
            error: {},
          },
        },
      });

      const api = promptManagement?.[feature]?.api;
      let res = null;
      if (!api) return;

      res = await api({
        feature: feature,
      });

      const Model = promptManagement?.[feature]?.model;
      let data = null;
      if (!Model) return;

      const sanitizer = promptManagement?.[feature]?.sanitizer;
      data = res?.clauses?.map(
        (res: any) => new Model({ ...res, sanitizer: sanitizer })
      );

      dispatch({
        type: ADMIN_ACTIONS.GET_PROMPTS,
        payload: {
          feature: feature,
          data: {
            data: data,
            loading: false,
            error: {},
          },
        },
      });
    } catch (error) {
      dispatch({
        type: ADMIN_ACTIONS.GET_PROMPTS,
        payload: {
          feature: feature,
          data: {
            loading: false,
            error: error,
          },
        },
      });
    }
  };
};

export const updateClause = ({
  feature,
  clauseId,
  name,
  value,
  isDefaultVal,
}: {
  feature: string;
  clauseId: string;
  name: string;
  value: string;
  isDefaultVal: boolean;
}) => {
  return async (dispatch: any, getState: any) => {
    try {
      await dispatch({
        type: ADMIN_ACTIONS.UPDATE_CLAUSE,
        payload: {
          feature: feature,
          clauseId: clauseId,
          name: name,
          value: value,
        },
      });

      const featureData =
        getState()?.rootReducer?.adminReducer?.[feature]?.data?.slice();
      const clauseIdx = featureData?.findIndex(
        (prompt: any) => prompt?.id === clauseId
      );

      if (clauseIdx > -1) {
        const rulesCopy = featureData[clauseIdx]?.rules?.slice();
        const ruleIdx = rulesCopy?.findIndex(
          (rule: any) => rule?.name === name
        );

        if (ruleIdx > -1) {
          await saveContractReviewClauseManagement({
            clause_id: clauseId,
            rules: getRuleForSavingForContractReview(rulesCopy),
          });
          isDefaultVal
            ? getToast("Error", "Clause couldn't be updated")
            : getToast("Success", "Clause updated successfully");
        }
      }
    } catch (error) {
      getToast("Error", "Clause couldn't be updated");
    }
  };
};

export const addNewKeywordSectionForTTC = () => {
  return async (dispatch: any) => {
    try {
      const newPrompt = {
        Word: "",
        Synonyms: [],
        type: defaultClauseStatus.NEW_KEYWORD,
        id: new Date().getTime(),
      };

      dispatch({
        type: ADMIN_ACTIONS.ADD_NEW_KEYWORD_SECTION_FOR_TTC,
        payload: {
          prompt: newPrompt,
        },
      });
    } catch (error) {}
  };
};

export const saveSynonym = () => {
  return async (dispatch: any, getState: any) => {
    try {
      const featureData =
        getState()?.rootReducer?.adminReducer?.[
          PromptsForFeature.TALK_TO_CONTRACT
        ]?.data?.slice();

      const ruleData = featureData[0];

      await saveTalkToContractClauseManagement({
        clause_id: ruleData?.id,
        rules: getRuleForSavingForTTC(ruleData?.rules),
      });
    } catch (error) {}
  };
};

export const saveNewSynonym = ({
  idx,
  data,
}: {
  idx: number;
  data: {
    Word: string;
    Synonyms: Array<string>;
    type: string;
  };
}) => {
  return async (dispatch: any) => {
    try {
      dispatch({
        type: ADMIN_ACTIONS.SAVE_NEW_SYNONYM_FOR_TTC,
        payload: {
          idx: idx,
          data: data,
        },
      });
      await dispatch(saveSynonym());
      getToast("Success", "Keyword added successfully");
    } catch (error) {
      getToast("Error", "Keyword couldn't be added");
    }
  };
};

export const deleteKeyword = ({ id }: { id: number }) => {
  return async (dispatch: any) => {
    try {
      dispatch({
        type: ADMIN_ACTIONS.DELETE_KEYWORD_SECTION_FOR_TTC,
        payload: {
          keywordId: id,
        },
      });
      await dispatch(saveSynonym());
      getToast("Success", "Keyword deleted successfully");
    } catch (error) {
      getToast("Error", "Keyword couldn't be deleted");
    }
  };
};

export const removeKeywordsWithNewKeywordType = () => {
  return async (dispatch: any) => {
    try {
      dispatch({
        type: ADMIN_ACTIONS.REMOVE_KEYWORDS_WITH_NEW_KEYWORD_TYPE,
      });
      await dispatch(saveSynonym());
    } catch (error) {}
  };
};

export const setDataForRoleManagement = (data: any) => {
  return async (dispatch: any) => {
    try {
      dispatch({
        type: ADMIN_ACTIONS.SET_ROLE_MANAGE_DATA,
        payload: data,
      });
    } catch (err) {}
  };
};
